import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, TextField, Typography, Snackbar, Alert, Grid, Modal } from '@mui/material';
import { WEBSOCKET_URL, API_URL } from '../../utils/constants';
import axios from 'axios';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { Highlight, themes } from 'prism-react-renderer';
import Prism from 'prismjs';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faXmark } from '@fortawesome/free-solid-svg-icons';
import { 
  faUserTie, // Business Analyst
  faNetworkWired, // Technical Architect
  faCode, // SWE
  faVial, // Tester
  faCloud, // Cloud Engineer
  faCheck, // Done
  faCirclePlay // start
} from '@fortawesome/free-solid-svg-icons';

interface LogLine {
  style: string;
  text: string;
  action: string;
}

export const Coder: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [codingLocation, setCodingLocation] = useState('');
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const logBoxRef = useRef<HTMLDivElement>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');
  const [artifactsTree, setArtifactsTree] = useState<any[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFileContent, setSelectedFileContent] = useState('');
  const [selectedFileLanguage, setSelectedFileLanguage] = useState('python');
  const [selectedFilePath, setSelectedFilePath] = useState('');
  const [activeRole, setActiveRole] = useState<string>('');

  const roles = [
    { icon: faCirclePlay, name: 'START', label: 'Start', color: '#ff4f58'},
    { icon: faUserTie, name: 'BUSINESS_ANALYST', label: 'Business Analyst', color: '#C8E6C9' },
    { icon: faNetworkWired, name: 'SOLUTIONS_ARCHITECT', label: 'Technical Architect', color: '#FFDAB9' },
    { icon: faCode, name: 'SERVERLESS_DEVELOPER', label: 'Developer', color: '#B3E5FC' },
    { icon: faVial, name: 'QA_ENGINEER', label: 'Tester', color: '#E6EE9C' },
    { icon: faCloud, name: 'CLOUD_ENGINEER', label: 'Cloud Engineer', color: '#ff4f58' },
    { icon: faCheck, name: 'DONE', label: 'Done', color: '#ff4f58' },
  ];

  useEffect(() => {
    const newSocket = new WebSocket(WEBSOCKET_URL);
    setActiveRole('START');

    newSocket.onopen = () => {
      console.log('WebSocket connected');
      setSocket(newSocket);
    };

    newSocket.onclose = () => {
      console.log('WebSocket disconnected');
      setSocket(null);
    };

    newSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    newSocket.onmessage = (event) => {
      const parsedMessage = JSON.parse(event.data);
      console.log('Received message from WebSocket:', parsedMessage);
      if (parsedMessage.action) {
        setLogLines((prevLogLines) => [
            ...prevLogLines,
            {
            style: parsedMessage && parsedMessage.status ? 
                    (parsedMessage.status.toLowerCase().includes('success') ? 'success' :
                    parsedMessage.status.toLowerCase().includes('error') ? 'error' :
                    parsedMessage.status.toLowerCase().includes('failed') ? 'error' :
                    parsedMessage.status.toLowerCase().includes('retry') ? 'notification' :
                    'general') : 'general',
            text: `${parsedMessage.action} - ${parsedMessage.status} - ${parsedMessage.message}`,
            action: parsedMessage.action
            },
        ]);
        setActiveRole(parsedMessage.action);
      } else if (String(parsedMessage.message).includes('Internal server error')) {
        setLogLines((prevLogLines) => [
            ...prevLogLines,
            {
            style: 'error',
            text: `${parsedMessage.message}`,
            action: ''
            },
        ]);
      }
    };

    return () => {
      newSocket.close();
    };
  }, []);

  useEffect(() => {
    console.log('Active role changed to:', activeRole);
  }, [activeRole]);

  useEffect(() => {
    loadArtifacts(codingLocation);
  }, [logLines]);

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [logLines]);

  useEffect(() => {
    // When artifactsTree changes, collect all node IDs to expand them
    const getAllNodeIds = (items: any[]): string[] => {
      let ids: string[] = [];
      items.forEach(item => {
        ids.push(item.id);
        if (item.children && item.children.length > 0) {
          ids = [...ids, ...getAllNodeIds(item.children)];
        }
      });
      return ids;
    };

    if (artifactsTree.length > 0) {
      const allIds = getAllNodeIds(artifactsTree);
      setExpandedNodes(allIds);
    }
  }, [artifactsTree]);

  const generateCodingLocation = async (): Promise<string> => {
    try {
      const ipResponse = await axios.get('https://api.ipify.org?format=json');
      const ip = ipResponse.data.ip;
      
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').pop();
      const now = new Date();
      const dateTime = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0');
      
      return `${ip}_${timezone}_${dateTime}`;
    } catch (error) {
      console.error('Error generating coding location:', error);
      // Fallback if IP fetch fails
      const fallbackIp = 'local';
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/').pop();
      const now = new Date();
      const dateTime = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0');
      
      return `${fallbackIp}_${timezone}_${dateTime}`;
    }
  };

  const handleSendQuestion = async () => {
    if (!question.trim()) {
      setSnackbarMessage("Please enter a question.");
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    const generatedLocation = await generateCodingLocation();
    setCodingLocation(generatedLocation);
    setActiveRole('START');

    if (socket && socket.readyState === WebSocket.OPEN) {
      const payload = JSON.stringify({
        action: 'CODER',
        question,
        coding_location: generatedLocation
      });
      setLogLines([]);
      socket.send(payload);
      console.log('Sent message to WebSocket:', question);
    } else {
      console.error('WebSocket is not connected');
    }
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const loadArtifacts = async (overrideCodingLocation: string = codingLocation) => {
    try {
      const response = await axios.get(`${API_URL}/codebases/${overrideCodingLocation}/available_artifacts`);
      console.log('Available artifacts:', response.data);
      const artifacts = response.data.codebase.files;
      // Transform artifacts into tree structure
      const buildTreeFromPaths = (paths: string[]) => {
        const tree: any = {};
        
        paths.forEach(path => {
          // Remove prefix before first slash and split remaining path
          const parts = path.split('/').slice(1);
          let current = tree;
          
          parts.forEach((part, index) => {
            if (!current[part]) {
              current[part] = index === parts.length - 1 ? null : {};
            }
            current = current[part];
          });
        });
        // Convert tree object to array format with id, label, children
        const convertToArray = (obj: any, parentPath: string = ''): any[] => {
          return Object.entries(obj)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => {
              const currentPath = parentPath ? `${parentPath}/${key}` : key;
              return {
                id: currentPath,
                label: key,
                children: value === null ? [] : convertToArray(value, currentPath)
              };
            });
        };

        return convertToArray(tree);
      };

      const treeData = buildTreeFromPaths(artifacts);
      console.log('Tree structure:', treeData);
      setArtifactsTree(treeData);
    } catch (error) {
      console.error('Error fetching artifacts:', error);
    }
  };

  const handleCodingLocationKeyPress = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && codingLocation.trim()) {
      loadArtifacts();
    }
  };

  const handleExpandedNodesChange = (event: React.SyntheticEvent, expandedItems: string[]) => {
    setExpandedNodes(expandedItems);
  };

  const handleArtifactClick = async (event: React.MouseEvent, artifact_path: string) => {
    console.log('Artifact clicked:', artifact_path);
    if (!artifact_path.includes('.')) {
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/codebases/${codingLocation}/available_artifacts/file?file_prefix=${encodeURIComponent(artifact_path)}`);
      setSelectedFileContent(response.data.content);
      setSelectedFileLanguage(getLanguageFromPath(artifact_path));
      setSelectedFilePath(artifact_path);
      setModalOpen(true);
    } catch (error) {
      console.error('Error fetching file content:', error);
    }
  };

  const getLanguageFromPath = (path: string): string => {
    const ext = path.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py':
        return 'python';
      case 'json':
        return 'json';
      case 'txt':
        return 'text';
      case 'ts':
      case 'tsx':
        return 'typescript';
      default:
        return 'typescript'; // fallback
    }
  };

  return (
    <Box sx={{ padding: '20px', paddingTop: '40px', paddingBottom: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '3rem',
        marginBottom: '40px' 
      }}>
        {roles.map((role) => (
          <Box
            key={role.name}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => setActiveRole(role.name)}
          >
            <FontAwesomeIcon
              icon={role.icon}
              style={{
                fontSize: '2.5rem',
                color: (() => {
                  const isActive = activeRole === role.name;
                  return isActive ? role.color : '#666';
                })(),
                transition: 'all 0.3s ease',
                opacity: activeRole === role.name ? 1 : 0.7,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.875rem',
                color: activeRole === role.name ? role.color : '#666',
                fontWeight: activeRole === role.name ? 600 : 400,
                transition: 'all 0.3s ease'
              }}
            >
              {role.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ marginBottom: '20px' }}>
        <TextField
          label="Enter your coding question"
          variant="outlined"
          value={question}
          name="bss_coding_question"
          onChange={(e) => setQuestion(e.target.value)}
          sx={{ width: '100%', marginBottom: '20px' }}
          multiline
          rows={4}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            disabled={!socket || socket.readyState !== WebSocket.OPEN} 
            onClick={handleSendQuestion}
            sx={{ width: '200px' }}
          >
            Submit Request
          </Button>
        </Box>
      </Box>
      <Box 
        id="logBox" 
        sx={{ 
          padding: '10px', 
          backgroundColor: '#1e1e1e', 
          height: 'calc(100vh - 452px)',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Grid container sx={{ 
          height: '100%',
          overflow: 'hidden'
        }}>
          <Grid 
            item 
            xs={12} 
            md={8} 
            ref={logBoxRef}
            sx={{ 
              height: '100%',
              overflowY: 'auto',
            }}
          >
            {logLines.slice().map((log, index) => (
              <Typography key={index} sx={{ 
                whiteSpace: 'pre-wrap', 
                marginLeft: '1rem', 
                marginTop: '.25rem', 
                textAlign: 'left', 
                fontFamily: 'Source Code Pro, monospace', 
                fontSize: '15px',
                color: log.style === 'info' ? '#6699CC' :
                      log.style === 'error' ? '#EC5f67' :
                      log.style === 'notification' ? '#FAC863' :
                      log.style === 'success' ? '#98fa63' :
                      log.style === 'success-partial' ? '#ffb700' :
                      log.style === 'fail' ? '#ff000d' :
                      log.action === 'QA_ENGINEER' ? '#E6EE9C' :
                      log.action === 'SERVERLESS_DEVELOPER' ? '#B3E5FC' :
                      log.action === 'SOLUTIONS_ARCHITECT' ? '#FFDAB9' :
                      log.action === 'BUSINESS_ANALYST' ? '#C8E6C9' :
                      log.style === 'general' ? '#fff' :
                      '#d4d4d4'
              }}>
                {log.text}
              </Typography>
            ))}
          </Grid>
          {artifactsTree && artifactsTree.length && <Grid item xs={12} md={4} sx={{ 
            pl: 2,
            height: '100%',
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              height: '100%',
              borderLeft: '1px solid #fff',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
              overflowY: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography variant="h6" sx={{ 
                color: '#fff',
                padding: '1rem',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                flexShrink: 0
              }}>
                Artifacts
              </Typography>
              <Box sx={{
                overflowY: 'auto',
                flex: 1,
                height: '100%'
              }}>
                <RichTreeView 
                  items={artifactsTree}
                  onItemClick={handleArtifactClick}
                  expandedItems={expandedNodes}
                  onExpandedItemsChange={handleExpandedNodesChange}
                  sx={{
                    display: 'block',
                    padding: '0.5rem',
                    '& .MuiTreeItem-root': {
                      color: '#fff'
                    },
                    '& .MuiTreeItem-label': {
                      fontSize: '0.9rem'
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>}
        </Grid>
      </Box>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="file-content-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{
          width: '80%',
          maxHeight: '80vh',
          bgcolor: '#1e1e1e',
          borderRadius: '8px',
          p: 4,
          overflow: 'auto'
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            paddingBottom: '1.2rem'
          }}>
            <Typography variant="h6" sx={{
              color: '#fff',
              padding: '0.4rem'
            }}>
              {selectedFilePath}
            </Typography>
            <Box sx={{
              display: 'flex',
              gap: 2
            }}>
              <Button
                title="Copy"
                onClick={() => {
                  navigator.clipboard.writeText(selectedFileContent);
                }}
                sx={{ 
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </Button>
              <Button 
                title="Close"
                onClick={() => setModalOpen(false)}
                sx={{ 
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </Box>
          </Box>
          <Box sx={{
            border: '1px solid #333',
            borderRadius: '4px',
            maxHeight: '60vh',
            overflow: 'auto'
          }}>
            <Highlight
              prism={Prism}
              theme={themes.vsDark}
              code={selectedFileContent}
              language={selectedFileLanguage}
            >
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <pre style={{ ...style, padding: '1rem', margin: 0, overflow: 'auto' }}>
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      <span style={{ display: 'inline-block', width: '2em', userSelect: 'none', opacity: 0.5 }}>
                        {i + 1}
                      </span>
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Coder;
